/** vendor */
import React, { useEffect, useState } from 'react'
import { PlusCircleIcon } from '@heroicons/react/solid'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

export default function SearchSelectForm({
    label = 'Search ...',
    buttonLabel = 'add',
    options = [], 
    selectedOptions = [],
    showTabs = false,
    showOptions = false,
    showCreateCustom = false,
    onCreateCustomItem = (option, index) => {}, 
    limit = 0,
    onSelectItem = () => { console.warn('SearchSelectForm: callback missing') },
    handleOnSelectSearchFocus  = () => {}
}) {
    const [allOptions, setAllOptions] = useState(options)
    const [filteredSortedOptions, setFilteredSortedOptions] = useState(options)
    const [currentTab, setCurrentTab] = useState(showTabs ? 'savedRecipes' : 'allOptions')
    const [currentSearch, setCurrentSearch] = useState('')

    const savedRecipes = useSelector(state => state?.user_data?.saved_recipes)
    const importedRecipes = useSelector(state => state?.user_data?.imported_recipes)
    const userRecipes = useSelector(state => state?.user_data?.user_recipes)
    const [myRecipes, setMyRecipes] = useState([])

    function getNameFromUrl(url) {
        try {
            const { pathname } = new URL(url);
            let cleanPath = pathname.split("#")[0].split("?")[0];
            let parts = cleanPath.split("/").filter(Boolean);
    
            // Filter out parts with digits or that look like random strings
            const titleParts = parts.filter(part =>
                /^[a-zA-Z-_]+$/.test(part) && part.length > 1 && part.length < 50
            );
    
            if (titleParts.length >= 2) {
                cleanPath = titleParts.slice(-2).join(' ');
            } else if (titleParts.length === 1) {
                cleanPath = titleParts[0];
            } else {
                cleanPath = parts.pop(); // fallback
            }
    
            return cleanPath.replace(/[-_]/g, " ").trim();
        } catch (error) {
            return url;
        }
    }

    /**
     * Hooks
     */
    useEffect(() => {
        if(options && options !== filteredSortedOptions) {
            setAllOptions(options)
            setFilteredSortedOptions(options)
        }
    }, [options, selectedOptions, userRecipes])

    useEffect(() => {
        if(savedRecipes?.length > 1 || importedRecipes?.length > 1 || userRecipes?.length > 1) {

            const formatImported = (importedRecipes ?? []).map((imported) => {                
                let importData = null

                try {
                    const parsedData = JSON.parse(imported?.data)
                    importData = parsedData

                    const formatted = {
                        category_id: importData?.message ? 'EXTERNAL_LINK' : 'IMPORTED',
                        comments: imported?.url,
                        description: importData?.description ?? '',
                        image: importData?.image && Array.isArray(importData?.image) ? importData?.image[0] : importData?.image ?? '',
                        name: importData?.name ?? getNameFromUrl(imported?.url),
                        recipe_id: imported?.import_recipe_id,
                        serves: imported?.recipeYield ?? 0,
                        verified: false,
                        ingredients: []
                    }
    
                    return formatted
                } catch (error) {
                    // continue
                    throw new Error('data is undefined')
                }
            })

            console.warn('userRecipes', userRecipes)
            let formatUserRecipes = []

            if(userRecipes && userRecipes?.length > 0) {
                formatUserRecipes = userRecipes?.map((userRecipe) => {
                    const formatted = {
                        category_id: 'USER', 
                        comments: null,
                        description: userRecipe?.description,
                        image: userRecipe?.image,
                        name: userRecipe?.name,
                        recipe_id: userRecipe?.user_recipe_id,
                        serves: userRecipe?.serves,
                        verified: false,
                        ingredients: userRecipe?.ingredients
                    }

                    return formatted
                })
            }

            const formatRecipes = [...savedRecipes, ...formatImported, ...formatUserRecipes].sort(
                (a, b) => a?.name.localeCompare(b?.name) 
            )
            
            setMyRecipes(formatRecipes)
        }
    }, savedRecipes, importedRecipes, userRecipes)

    /**
     * Handlers
     */
    const handleSearch = e => {
        if(e.target.value.length < 1) {
            let optionsLimit = [...options]

            setFilteredSortedOptions(allOptions)
            return
        }

        if(e.target.value.length > 2) {
            let filteredOptions = [...allOptions].filter(i => {
                const lowerName = i.name.toLowerCase().trim()
                const lowerValue = e.target.value.toLowerCase().trim()
                return lowerName === lowerValue || lowerName.includes(lowerValue)
            })
            
            setFilteredSortedOptions(filteredOptions)
        }
    }

    const handleSelection = (option, index) => {
        onSelectItem(index, option)
        setCurrentSearch('')
    }

    const handleCreateCustomItem = () => {
        onCreateCustomItem(null, {name: currentSearch, quantity: 1, image: null, value: 'CUSTOM_' + uuidv4()})
        setCurrentSearch('')        
    }

    return (
        <div className="w-full">
            <div className={`w-full ${showOptions ? 'p-2' : ''}`}>
                {
                    showOptions ?
                        <div className="bg-an_white overflow-hidden rounded-lg w-full">
                            {
                                showTabs &&
                                    <h2 className="w-full text-xl text-an_white text-center items-center p-4 bg-an_green-dark rounded-t mb-2">
                                        add recipes
                                    </h2>
                            }

                            {
                                showTabs &&
                                    <ul className="flex px-1 text-sm font-medium text-center border-b border-an_orange-dark overflow-hidden rounded-t-lg">
                                        <li className="w-full">
                                            <a href="#" 
                                                onClick={() => { setCurrentTab('savedRecipes') }}
                                                className={`${currentTab === 'savedRecipes' ? 'active bg-an_orange border-an_orange-dark text-an_white text-border-orange' : 'text-an_orange-dark'} block py-1 px-2 rounded-t`}
                                            >My Recipes</a>
                                        </li>
                                        <li className="w-full">
                                            <a href="#" 
                                                onClick={() => { setCurrentTab('allOptions') }}
                                                className={`${currentTab === 'allOptions' ? 'active bg-an_orange border-an_orange-dark text-an_white text-border-orange' : 'text-an_orange-dark'} block py-1 px-2 rounded-t`}
                                            >All Recipes</a>
                                        </li>
                                    </ul>
                            }

                            { 
                                showTabs && currentTab === 'savedRecipes' && 
                                    <ul className="overflow-y-auto text-sm h-[355px]">
                                        {
                                            myRecipes && myRecipes.length > 0 ?
                                                myRecipes.map((option, index) => (
                                                    <li 
                                                        key={`select-search-option-${index}`} 
                                                        className={`odd:bg-an_white-dark hover:bg-an_white-darker1 py-1  pr-2`}
                                                    >
                                                        <div className="flex items-center pl-2 rounded">
                                                            <label 
                                                                className="w-full py-2 text-base text-an_green-dark"
                                                            >
                                                                {option.name}
                                                            </label>
                                                            <button
                                                                onClick={() => { handleSelection(option, index)} }
                                                                className="w-6 h-6 ml-auto mr-1 text-an_green"
                                                            >
                                                                <PlusCircleIcon className="text-an_green" />
                                                            </button>
                                                        </div>
                                                        {
                                                            ['IMPORTED', 'EXTERNAL_LINK', 'USER'].includes(option?.category_id) &&
                                                                <small className='text-an_green-dark text-xs px-2 pb-2 leading-none mt-[-10px] block'>
                                                                    {option?.category_id === 'IMPORTED' && 'imported'}
                                                                    {option?.category_id === 'EXTERNAL_LINK' && 'from the web'}
                                                                    {option?.category_id === 'USER' && 'my recipe'}
                                                                </small>
                                                        }
                                                    </li> 
                                                )) : 
                                                <li>                        
                                                    <div className="flex items-center pl-2 rounded">
                                                        <label 
                                                            className="w-full py-2 text-sm font-medium text-black rounded"
                                                        >
                                                            No Options Found ...
                                                        </label>
                                                    </div>
                                                </li>                           
                                        } 
                                    </ul> 
                            }

                            { 
                                currentTab === 'allOptions' && 
                                    <ul className=" overflow-y-auto text-sm h-[300px]">
                                        {
                                            filteredSortedOptions && filteredSortedOptions.length > 0 ?
                                                filteredSortedOptions.map((option, index) => (
                                                    <li 
                                                        key={`select-search-option-${index}`} 
                                                        className={`odd:bg-an_white-dark hover:bg-an_white-darker1 py-1 pr-2`}
                                                    >
                                                        <div className="flex items-center pl-2">
                                                            <label 
                                                                className="w-full py-2 text-base text-an_green-dark"
                                                            >
                                                                {option.name}
                                                            </label>
                                                            <button
                                                                onClick={() => { handleSelection(option, index)} }
                                                                className="w-6 h-6 ml-auto mr-1 text-an_green"
                                                            >
                                                                <PlusCircleIcon className="text-an_green" />
                                                            </button>
                                                        </div>
                                                    </li> 
                                                )) : 
                                                <li>                        
                                                    <div className="flex items-center pl-2 rounded">
                                                        <label 
                                                            className="w-full py-6 text-base text-center text-an_green-dark"
                                                        >
                                                            ziltch.
                                                        </label>
                                                    </div>
                                                </li>                           
                                        }    
                                    </ul> 
                            }

                            { 
                                currentTab === 'allOptions' &&
                                    <div className="p-2 border-t border-an_green-dark bg-an_white-dark">
                                        <label htmlFor="input-group-search" className="sr-only">{label}</label>

                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                <svg 
                                                    className="w-5 h-5 text-an_green" 
                                                    aria-hidden="true" 
                                                    fill="currentColor" 
                                                    viewBox="0 0 20 20" 
                                                    xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                                                </svg>
                                            </div>
                                            <input 
                                                type="text" 
                                                className="block w-full p-2 pl-10 text-sm rounded" 
                                                placeholder={label}
                                                value={currentSearch} 
                                                onChange={(e) => {
                                                    setCurrentSearch(e.target.value) 
                                                    handleSearch(e) 
                                                }}
                                            />
                                            { 
                                                showCreateCustom && 
                                                    <button
                                                        disabled={filteredSortedOptions?.length > 0}
                                                        onClick={() => { handleCreateCustomItem() } }
                                                        className={`w-6 h-6 absolute inset-y-0 top-[6px] right-1 ${ filteredSortedOptions?.length > 0 && 'opacity-20' }`}
                                                    >
                                                        <PlusCircleIcon className="text-an_green" />
                                                    </button>
                                            }
                                        </div>
                                    </div> 
                            }                            
                        </div> :   
                        <div className="pb-4 flex justify-center">
                            <button 
                                className="bg-an_orange w-36 text-border-orange min-w-32 text-an_white rounded font-medium border border-an_orange-darker3 text-sm px-5 py-2"
                                onClick={(e) => { handleOnSelectSearchFocus(e) }}>{buttonLabel}</button>
                        </div>
                }
            </div>
        </div>
    )
}
